import { useEffect, useState } from 'react';
import { campaign } from './campaign';
import ModalCta from './ModalCta';
import '../../styles/Campaign.css';
import '../../styles/CampaignDock.css';

const CLAVE_MOSTRADO = 'cn_campana_dock_mostrada';

// HU-016: `showOncePer` decide si el dock se auto-abre al cargar el mapa —
// una vez mostrado en el período elegido, las siguientes cargas arrancan
// directo en la píldora minimizada, sin la animación de apertura
// automática. El clic manual en la píldora siempre reabre, sin importar
// este gateo (es solo para no repetir la apertura sola cada recarga).
const yaSeMostroEnEstePeriodo = () => {
  if (campaign.showOncePer === 'always') return false;
  const storage = campaign.showOncePer === 'day' ? localStorage : sessionStorage;
  const valor = storage.getItem(CLAVE_MOSTRADO);
  if (campaign.showOncePer === 'day') return valor === new Date().toDateString();
  return valor === '1';
};

const marcarMostrado = () => {
  if (campaign.showOncePer === 'always') return;
  const storage = campaign.showOncePer === 'day' ? localStorage : sessionStorage;
  storage.setItem(CLAVE_MOSTRADO, campaign.showOncePer === 'day' ? new Date().toDateString() : '1');
};

/**
 * Dock flotante de campaña (HU-016, lámina 01/02) — solo desktop (≥768px,
 * ver CampaignDock.css). Vive dentro de `.mapa-principal` como una capa más
 * (`position: absolute`), nunca mueve ni redimensiona el buscador, los
 * chips, el zoom ni el riel de navegación — regla no negociable del diseño
 * aprobado por el cliente. Sin `configuracion.banner`, no renderiza nada.
 */
const CampaignDock = ({ configuracion }) => {
  const [abierto, setAbierto] = useState(false);
  const [duracionMs, setDuracionMs] = useState(campaign.autoCollapseMs);
  const [corriendoBarra, setCorriendoBarra] = useState(true);
  const [barraKey, setBarraKey] = useState(0);
  const [modalCta, setModalCta] = useState(null);

  useEffect(() => {
    if (!configuracion.banner) return undefined;
    // HU-034: en móvil arranca SIEMPRE maximizado; en escritorio, una vez por
    // período (según `campaign.showOncePer`).
    const esMovil = typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;
    if (!esMovil && yaSeMostroEnEstePeriodo()) return undefined;
    const id = setTimeout(() => {
      setAbierto(true);
      marcarMostrado();
    }, esMovil ? 400 : 900);
    return () => clearTimeout(id);
  }, [configuracion.banner]);

  if (!configuracion.banner) return null;

  const abrir = () => {
    setAbierto(true);
    setDuracionMs(campaign.autoCollapseMs);
    setCorriendoBarra(true);
    setBarraKey((k) => k + 1);
  };

  // "mouseenter pausa y resetea la barra" — se remonta (key nueva) ya en
  // pausa, así arranca siempre desde el 100% la próxima vez que corra.
  const pausarYResetear = () => {
    setCorriendoBarra(false);
    setBarraKey((k) => k + 1);
  };

  // "mouseleave reinicia la cuenta a 5s" — mismo elemento (ya reseteado al
  // 100% por el hover), solo cambia la duración y lo deja correr.
  const reanudarConCincoSegundos = () => {
    setDuracionMs(5000);
    setCorriendoBarra(true);
  };

  const altBanner = `${campaign.party} — ${campaign.candidate}${configuracion.slogan ? `, ${configuracion.slogan}` : ''}`;

  // HU-019/021: CTAs y pilares configurables (texto, URL, modal y visibilidad).
  const ctaTexto = configuracion.campana_cta_texto || campaign.ctaLabel;
  const ctaUrl = configuracion.campana_cta_url || campaign.ctaUrl;
  const cta2Texto = configuracion.campana_cta2_texto || 'Ver obras';
  const cta2Url = configuracion.campana_cta2_url || '';
  const cta3Texto = configuracion.campana_cta3_texto || 'Mis obras';
  const cta3Url = configuracion.campana_cta3_url || '';
  const cta4Texto = configuracion.campana_cta4_texto || 'Propuestas';
  const cta4Url = configuracion.campana_cta4_url || '';
  const mostrarCta1 = configuracion.campana_cta_activo !== false;
  const mostrarCta2 = !!configuracion.campana_cta2_activo;
  const mostrarCta3 = !!configuracion.campana_cta3_activo;
  const mostrarCta4 = !!configuracion.campana_cta4_activo;

  // HU-025: cada pilar tiene texto y URL configurables (URL → clickeable).
  const pilaresConfig = [
    { activo: configuracion.campana_pilar_seguridad, texto: configuracion.campana_pilar_1_texto || 'Seguridad', url: configuracion.campana_pilar_1_url || '' },
    { activo: configuracion.campana_pilar_orden, texto: configuracion.campana_pilar_2_texto || 'Orden', url: configuracion.campana_pilar_2_url || '' },
    { activo: configuracion.campana_pilar_experiencia, texto: configuracion.campana_pilar_3_texto || 'Experiencia', url: configuracion.campana_pilar_3_url || '' },
  ];
  const pilaresVisibles = pilaresConfig.filter((p) => p.activo !== false && p.texto);
  const mostrarPilares = configuracion.campana_pilares_activo !== false && pilaresVisibles.length > 0;

  const abrirCta = (url, enModal) => {
    if (!url) return;
    if (enModal) setModalCta(url);
    else window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className={`cd-dock ${abierto ? 'cd-dock-abierto' : 'cd-dock-minimizado'}`}
      onMouseEnter={abierto ? pausarYResetear : undefined}
      onMouseLeave={abierto ? reanudarConCincoSegundos : undefined}
    >
      {abierto ? (
        <div className="cd-contenido-abierto cn-shine">
          <button
            type="button"
            className="cd-minimizar"
            onClick={() => setAbierto(false)}
            aria-label="Minimizar"
            aria-expanded="true"
          >
            Minimizar <i className="fas fa-chevron-down"></i>
          </button>

          <img src={configuracion.banner} alt={altBanner} className="cd-banner cn-kb" loading="lazy" />

          <div className="cd-fila-inferior">
            {mostrarPilares && (
              <div className="cd-pilares">
                {pilaresVisibles.map((p) => (
                  p.url ? (
                    <a
                      key={p.texto}
                      className="cd-pilar-chip cd-pilar-chip-link"
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', cursor: 'pointer' }}
                    >
                      {p.texto}
                    </a>
                  ) : (
                    <span key={p.texto} className="cd-pilar-chip">{p.texto}</span>
                  )
                ))}
              </div>
            )}
            {mostrarCta1 && (
              <button
                type="button"
                className="cd-cta"
                disabled={!ctaUrl}
                onClick={() => abrirCta(ctaUrl, configuracion.campana_cta_modal)}
              >
                {ctaTexto} →
              </button>
            )}
            {mostrarCta2 && (
              <button
                type="button"
                className="cd-cta"
                disabled={!cta2Url}
                onClick={() => abrirCta(cta2Url, configuracion.campana_cta2_modal)}
              >
                {cta2Texto} →
              </button>
            )}
            {mostrarCta3 && (
              <button
                type="button"
                className="cd-cta"
                disabled={!cta3Url}
                onClick={() => abrirCta(cta3Url, configuracion.campana_cta3_modal)}
              >
                {cta3Texto} →
              </button>
            )}
            {mostrarCta4 && (
              <button
                type="button"
                className="cd-cta"
                disabled={!cta4Url}
                onClick={() => abrirCta(cta4Url, configuracion.campana_cta4_modal)}
              >
                {cta4Texto} →
              </button>
            )}
          </div>

          <div
            key={barraKey}
            className="cd-barra-progreso"
            style={{ animationDuration: `${duracionMs}ms`, animationPlayState: corriendoBarra ? 'running' : 'paused' }}
            onAnimationEnd={() => setAbierto(false)}
          ></div>
        </div>
      ) : (
        <button
          type="button"
          className="cd-contenido-pildora"
          onClick={abrir}
          aria-label="Mostrar banner de campaña"
          aria-expanded="false"
        >
          <span className="cd-pildora-logo">
            {configuracion.logo && <img src={configuracion.logo} alt="" />}
            <span className="cn-anillo-pulso cd-pildora-anillo"></span>
          </span>
          <span className="cd-pildora-texto">
            <strong>{campaign.party}</strong>
            <span>{configuracion.slogan || campaign.headline}</span>
          </span>
          <i className="fas fa-chevron-up"></i>
        </button>
      )}

      {modalCta && <ModalCta url={modalCta} onCerrar={() => setModalCta(null)} />}
    </div>
  );
};

export default CampaignDock;
